import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  addMyDealMessage,
  correctDeal,
  deleteDealDocument,
  uploadDealDocuments,
  createDraft,
  createNewsItem,
  deleteDraft,
  deleteNewsItem,
  getDeal,
  getDealOwners,
  getDealSummary,
  getDeals,
  getEditorNews,
  getMyDeal,
  getMyDealSummary,
  getMyDeals,
  getNews,
  getNewsItem,
  processDeal,
  submitDeal,
  updateDraft,
  updateNewsItem,
  withdrawDeal,
} from "../Api/ambassador";
import {
  Deal,
  DealData,
  DealListFilters,
  InternalDeal,
  InternalDealListFilters,
  NewsCategory,
  NewsPayload,
  NewsStatus,
  ProcessingPayload,
} from "../Models/ambassador";

// The ambassador's own deals

export const useMyDeals = (filters: DealListFilters) =>
  useQuery({
    queryKey: ["ambassadorMyDeals", filters],
    queryFn: () => getMyDeals(filters),
    placeholderData: keepPreviousData,
  });

export const useMyDealSummary = () =>
  useQuery({
    queryKey: ["ambassadorMyDealSummary"],
    queryFn: getMyDealSummary,
  });

export const useMyDeal = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["ambassadorMyDeal", dealId],
    queryFn: () => getMyDeal(dealId as number),
    enabled: !!dealId,
  });

const useMyDealMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<Deal>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (deal) => {
      queryClient.setQueryData(["ambassadorMyDeal", deal.id], deal);
      queryClient.invalidateQueries({ queryKey: ["ambassadorMyDeals"] });
      queryClient.invalidateQueries({ queryKey: ["ambassadorMyDealSummary"] });
    },
  });
};

export const useSaveDraft = () =>
  useMyDealMutation(
    (variables: {
      dealId: number | null;
      data: Partial<DealData>;
      notify: boolean;
    }) =>
      variables.dealId === null
        ? createDraft(variables.data, variables.notify)
        : updateDraft(variables.dealId, variables.data, variables.notify),
  );

export const useSubmitDeal = () =>
  useMyDealMutation((variables: { dealId: number; data: Partial<DealData> }) =>
    submitDeal(variables.dealId, variables.data),
  );

export const useWithdrawDeal = () =>
  useMyDealMutation((variables: { dealId: number; reason: string }) =>
    withdrawDeal(variables.dealId, variables.reason),
  );

export const useUploadMyDealDocuments = () =>
  useMyDealMutation((variables: { dealId: number; files: File[] }) =>
    uploadDealDocuments<Deal>("ambassador", variables.dealId, variables.files),
  );

export const useDeleteMyDealDocument = () =>
  useMyDealMutation((variables: { dealId: number; documentId: number }) =>
    deleteDealDocument<Deal>(
      "ambassador",
      variables.dealId,
      variables.documentId,
    ),
  );

export const useAddMyDealMessage = () =>
  useMyDealMutation((variables: { dealId: number; message: string }) =>
    addMyDealMessage(variables.dealId, variables.message),
  );

export const useDeleteDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dealId: number) => deleteDraft(dealId),
    onSuccess: (_, dealId) => {
      queryClient.removeQueries({ queryKey: ["ambassadorMyDeal", dealId] });
      queryClient.invalidateQueries({ queryKey: ["ambassadorMyDeals"] });
      queryClient.invalidateQueries({ queryKey: ["ambassadorMyDealSummary"] });
    },
  });
};

// como's deal management

export const useDeals = (filters: InternalDealListFilters) =>
  useQuery({
    queryKey: ["ambassadorDeals", filters],
    queryFn: () => getDeals(filters),
    placeholderData: keepPreviousData,
  });

export const useDealSummary = () =>
  useQuery({
    queryKey: ["ambassadorDealSummary"],
    queryFn: getDealSummary,
  });

export const useDealOwners = () =>
  useQuery({
    queryKey: ["ambassadorDealOwners"],
    queryFn: getDealOwners,
  });

export const useDeal = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["ambassadorDeal", dealId],
    queryFn: () => getDeal(dealId as number),
    enabled: !!dealId,
  });

const useDealMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<InternalDeal>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (deal) => {
      queryClient.setQueryData(["ambassadorDeal", deal.id], deal);
      queryClient.invalidateQueries({ queryKey: ["ambassadorDeals"] });
      queryClient.invalidateQueries({ queryKey: ["ambassadorDealSummary"] });
    },
  });
};

export const useProcessDeal = () =>
  useDealMutation((variables: { dealId: number; data: ProcessingPayload }) =>
    processDeal(variables.dealId, variables.data),
  );

export const useUploadDealDocuments = () =>
  useDealMutation((variables: { dealId: number; files: File[] }) =>
    uploadDealDocuments<InternalDeal>(
      "como",
      variables.dealId,
      variables.files,
    ),
  );

export const useDeleteDealDocument = () =>
  useDealMutation((variables: { dealId: number; documentId: number }) =>
    deleteDealDocument<InternalDeal>(
      "como",
      variables.dealId,
      variables.documentId,
    ),
  );

export const useCorrectDeal = () =>
  useDealMutation((variables: { dealId: number; data: Partial<DealData> }) =>
    correctDeal(variables.dealId, variables.data),
  );

// News

export const useNews = (filters: {
  page: number;
  page_size?: number;
  category?: NewsCategory | null;
}) =>
  useQuery({
    queryKey: ["ambassadorNews", filters],
    queryFn: () => getNews(filters),
    placeholderData: keepPreviousData,
  });

export const useNewsItem = (newsId: number | undefined) =>
  useQuery({
    queryKey: ["ambassadorNewsItem", newsId],
    queryFn: () => getNewsItem(newsId as number),
    enabled: !!newsId,
  });

export const useEditorNews = (filters: {
  page: number;
  page_size?: number;
  status?: NewsStatus | null;
}) =>
  useQuery({
    queryKey: ["ambassadorEditorNews", filters],
    queryFn: () => getEditorNews(filters),
    placeholderData: keepPreviousData,
  });

const useNewsMutation = <TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ambassadorEditorNews"] });
      queryClient.invalidateQueries({ queryKey: ["ambassadorNews"] });
      queryClient.invalidateQueries({ queryKey: ["ambassadorNewsItem"] });
    },
  });
};

export const useSaveNewsItem = () =>
  useNewsMutation((variables: { newsId: number | null; data: NewsPayload }) =>
    variables.newsId === null
      ? createNewsItem(variables.data)
      : updateNewsItem(variables.newsId, variables.data),
  );

export const useDeleteNewsItem = () =>
  useNewsMutation((newsId: number) => deleteNewsItem(newsId));
